import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

const TITLE = "Datenschutzerklärung, shopify-freelancer.net";
const DESCRIPTION = "Informationen zur Verarbeitung personenbezogener Daten auf shopify-freelancer.net.";

export const Route = createFileRoute("/datenschutz")({
  component: DatenschutzPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://shopify-freelancer.net/datenschutz" },
    ],
    links: [{ rel: "canonical", href: "https://shopify-freelancer.net/datenschutz" }],
  }),
});

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="py-6">
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      <div className="mt-2 space-y-2 text-sm text-muted-foreground">{children}</div>
    </section>
  );
}

function DatenschutzPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 py-16">
        <h1 className="text-3xl font-bold md:text-4xl">Datenschutzerklärung</h1>

        <div className="mt-6 divide-y divide-border/60">
          <Block title="1. Verantwortliche Stelle">
            <p>Nicolai Schnepf, Karlstr. 42, 76133 Karlsruhe, Deutschland</p>
            <p>
              E-Mail:{" "}
              <a
                href="mailto:info@shopify-freelancer.net"
                className="text-primary underline underline-offset-4"
              >
                info@shopify-freelancer.net
              </a>
            </p>
          </Block>


          <Block title="2. Hosting">
            <p>
              Diese Website wird bei einem externen Dienstleister gehostet. Beim Aufruf der Seite
              werden automatisch Server-Logfiles verarbeitet (IP-Adresse, Datum und Uhrzeit,
              aufgerufene Seite, Browsertyp, Betriebssystem, Referrer). Rechtsgrundlage ist Art. 6
              Abs. 1 lit. f DSGVO, berechtigtes Interesse am sicheren und stabilen Betrieb.
            </p>
          </Block>

          <Block title="3. Kontaktaufnahme">
            <p>
              Wenn Sie mich per Formular oder E-Mail kontaktieren, werden die von Ihnen angegebenen
              Daten (Name, E-Mail-Adresse, Shop-URL, Nachricht) zur Bearbeitung der Anfrage
              verarbeitet. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche
              Maßnahmen) bzw. lit. f DSGVO. Die Daten werden gelöscht, sobald sie für den Zweck
              nicht mehr erforderlich sind und keine gesetzlichen Aufbewahrungsfristen entgegenstehen.
            </p>
          </Block>

          <Block title="4. Reichweitenmessung und Werbedienste">
            <p>
              Zur Auswertung der Nutzung dieser Website und zur Messung des Erfolgs von
              Werbekampagnen können Dienste der Google Ireland Limited (Gordon House, Barrow
              Street, Dublin 4, Irland) eingesetzt werden. Dabei können Cookies gesetzt und Daten 
              gegebenenfalls auch an Google LLC in den USA, übermittelt werden. Verarbeitet werden
              pseudonymisierte Nutzungsdaten wie aufgerufene Seiten, Verweildauer, ungefährer
              Standort und Geräteinformationen. Rechtsgrundlage ist Ihre Einwilligung nach Art. 6
              Abs. 1 lit. a DSGVO bzw. § 25 Abs. 1 TDDDG; Sie können diese jederzeit mit Wirkung für
              die Zukunft widerrufen. Weitere Informationen:{" "}
              <a
                href="https://policies.google.com/privacy"
                className="text-primary underline underline-offset-4"
                rel="noopener noreferrer"
                target="_blank"
              >
                policies.google.com/privacy
              </a>
              .
            </p>
          </Block>

          <Block title="5. Cookies">
            <p>
              Technisch notwendige Cookies werden auf Grundlage von § 25 Abs. 2 TDDDG gesetzt. Alle
              weiteren Cookies, insbesondere für Statistik und Marketing, werden nur mit Ihrer
              Einwilligung verwendet. Sie können Cookies in Ihren Browsereinstellungen jederzeit
              löschen oder blockieren.
            </p>
          </Block>

          <Block title="6. Ihre Rechte">
            <p>
              Sie haben das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung
              (Art. 17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20)
              sowie Widerspruch gegen die Verarbeitung (Art. 21). Erteilte Einwilligungen können Sie
              jederzeit widerrufen. Außerdem steht Ihnen ein Beschwerderecht bei einer
              Datenschutz-Aufsichtsbehörde zu.
            </p>
          </Block>

          <Block title="7. Datensicherheit">
            <p>
              Diese Website nutzt eine SSL-/TLS-Verschlüsselung, erkennbar an „https://" in der
              Adresszeile Ihres Browsers. Damit sind die Daten, die Sie an mich übermitteln, für
              Dritte nicht mitlesbar.
            </p>
          </Block>

          <Block title="8. Aktualität">
            <p>
              Diese Datenschutzerklärung wird angepasst, sobald sich Rechtslage oder eingesetzte
              Dienste ändern. Stand: {new Date().getFullYear()}.
            </p>
          </Block>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
